import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase/supabase';
import { useTenant } from './TenantContext';

const CRMContext = createContext();
export const useCRM = () => useContext(CRMContext);

const isProduction = import.meta.env.PROD === true;

const SALESWORK_COMPANIES_KEY = 'saleswork_demo_companies';
const SALESWORK_CONTACTS_KEY = 'saleswork_demo_contacts';
const SALESWORK_DEALS_KEY = 'saleswork_demo_deals';

const loadFromStorage = (key, fallback = []) => {
  try {
    if (typeof localStorage === 'undefined') return fallback;

    const saved = localStorage.getItem(key);
    if (!saved) return fallback;

    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    console.error('[CRM STORAGE] Failed to load', key, error);
    return fallback;
  }
};

const saveToStorage = (key, value) => {
  try {
    if (typeof localStorage === 'undefined') return;

    localStorage.setItem(
      key,
      JSON.stringify(Array.isArray(value) ? value : [])
    );
  } catch (error) {
    console.error('[CRM STORAGE] Failed to save', key, error);
  }
};

const initialPreviewMode = !isProduction && (
  import.meta.env.VITE_PREVIEW_MODE === 'true' ||
  (typeof window !== 'undefined' &&
    (window.localStorage?.getItem('saleswork_preview_mode') === 'true' ||
      window.location.search.includes('preview=1') ||
      window.location.hash.includes('preview=1')))
);

const previewUser = {
  user_id: 'preview-superadmin',
  email: 'preview@local.saleswork.no',
  name: 'Local SuperAdmin Preview',
  role: 'SuperAdmin',
  appMetadataRole: 'SuperAdmin',
  isSuperAdmin: true
};

const demoAccounts = [
  {
    id: 'demo-start',
    email: 'start.demo@saleswork.no',
    password: 'demo-start',
    name: 'Start Demo',
    plan: 'Start',
    planId: 'start',
    tenantName: 'Start Demo AS'
  },
  {
    id: 'demo-pro',
    email: 'vekst.demo@saleswork.no',
    password: 'demo-vekst',
    name: 'Vekst Demo',
    plan: 'Vekst',
    planId: 'pro',
    tenantName: 'Vekst Demo AS'
  },
  {
    id: 'demo-enterprise',
    email: 'enterprise.demo@saleswork.no',
    password: 'demo-enterprise',
    name: 'Enterprise Demo',
    plan: 'Enterprise',
    planId: 'enterprise',
    tenantName: 'Enterprise Demo AS'
  }
];

const defaultTenant = {
  id: 'demo-tenant',
  name: 'Sales Work',
  logo: '⚡',
  plan: 'Start',
  planId: 'start'
};

const previewCustomers = [
  {
    id: 'preview-customer-1',
    company_name: 'Nordic Growth AS',
    contact_email: 'kunde@nordicgrowth.no',
    plan: 'Professional',
    status: 'Active',
    seats: 12,
    monthly_value: 14900,
    started_at: '2025-01-15'
  },
  {
    id: 'preview-customer-2',
    company_name: 'Fjordkraft Consulting',
    contact_email: 'admin@fjordkraft.no',
    plan: 'Enterprise',
    status: 'Active',
    seats: 28,
    monthly_value: 29900,
    started_at: '2024-11-01'
  },
  {
    id: 'preview-customer-3',
    company_name: 'Demo Kunde AS',
    contact_email: 'demo@example.com',
    plan: 'Trial',
    status: 'PastDue',
    seats: 3,
    monthly_value: 0,
    started_at: '2025-02-10'
  }
];

const previewProfiles = [
  {
    id: 'preview-profile-1',
    email: 'admin@nordicgrowth.no',
    name: 'Nordic Growth Admin',
    role: 'Customer',
    status: 'Active'
  },
  {
    id: 'preview-profile-2',
    email: 'owner@fjordkraft.no',
    name: 'Fjordkraft Owner',
    role: 'Customer',
    status: 'Active'
  },
  {
    id: 'preview-profile-3',
    email: 'preview@local.saleswork.no',
    name: 'Local SuperAdmin Preview',
    role: 'SuperAdmin',
    status: 'Active'
  }
];

const previewAuditLog = [
  {
    id: 'preview-audit-1',
    action: 'customer.updated',
    entity_type: 'platform_customer',
    created_at: new Date().toISOString(),
    details: { status: 'Active' }
  },
  {
    id: 'preview-audit-2',
    action: 'platform.profile_created',
    entity_type: 'platform_profile',
    created_at: new Date(Date.now() - 86400000).toISOString(),
    details: { source: 'preview' }
  }
];

const getActivityRow = (activity, userId, tenantId) => ({
  title: activity.title?.trim() || '',
  type: activity.type || 'Oppgave',
  status: activity.status || 'Planlagt',
  date: activity.date || new Date().toISOString(),
  end_date: activity.end_date || activity.endDate || null,
  platform: activity.platform || '',
  meeting_link: activity.meeting_link || '',
  contact_id: activity.contact_id || null,
  company_id: activity.company_id || activity.companyId || null,
  deal_id: activity.deal_id || null,
  user_id: activity.user_id || userId || null,
  tenant_id: tenantId || null,
  description: activity.description || activity.notes || activity.content || ''
});

const createId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const isUuid = value => (
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
);

const getEntityId = (entity, fallback) => (
  entity?.id ||
  entity?.deal_id ||
  entity?.company_id ||
  entity?.contact_id ||
  fallback
);

export const CRMProvider = ({ children }) => {
  const { activeTenantId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [previewMode, setPreviewMode] = useState(initialPreviewMode);
  const [currentTenant, setCurrentTenant] = useState(defaultTenant);
  const [tenants, setTenants] = useState([defaultTenant]);

  const [companies, setCompanies] = useState(() => {
    const savedCompanies = loadFromStorage(SALESWORK_COMPANIES_KEY, []);
    if (!isProduction) {
      console.log('[COMPANY LOAD] localStorage companies loaded', savedCompanies);
    }
    return savedCompanies;
  });
  const [contacts, setContacts] = useState(() =>
    loadFromStorage(SALESWORK_CONTACTS_KEY, [])
  );
  const [deals, setDeals] = useState(() =>
    loadFromStorage(SALESWORK_DEALS_KEY, [])
  );
  const [activities, setActivities] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [platformProfiles, setPlatformProfiles] = useState([]);
  const [platformCustomers, setPlatformCustomers] = useState([]);
  const [platformAuditLog, setPlatformAuditLog] = useState([]);
  const [platformEmployeeInvites, setPlatformEmployeeInvites] = useState([]);
  const [superAdminLoading, setSuperAdminLoading] = useState(false);

  const getActiveTenantId = () => (
    activeTenantId &&
    activeTenantId !== 'demo-tenant' &&
    isUuid(activeTenantId)
      ? activeTenantId
      : null
  );

  const tenantRequiredError = () => ({
    success: false,
    error: {
      message: 'Mangler gyldig aktiv tenant. CRM-data ble ikke skrevet.'
    }
  });

  useEffect(() => {
    if (isProduction && typeof localStorage !== 'undefined') {
      localStorage.removeItem('saleswork_preview_mode');
    }

    if (!isProduction) {
      console.log('[CRM AUTH MODE]', {
        previewMode,
        isDev: import.meta.env.DEV,
        explicitPreviewEnv: import.meta.env.VITE_PREVIEW_MODE
      });
    }

    if (!isProduction && previewMode) {
      setCurrentUser(previewUser);
      setIsAuthenticated(true);
      setPlatformProfiles(previewProfiles);
      setPlatformCustomers(previewCustomers);
      setPlatformAuditLog(previewAuditLog);
      setLoading(false);
      return undefined;
    }

    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      handleAuthChange(session);
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isProduction) {
        console.log('[AUTH STATE CHANGE]', {
          event,
          hasSession: !!session,
          userEmail: session?.user?.email
        });
      }
      handleAuthChange(session);
    });
    initAuth();
    return () => subscription.unsubscribe();
  }, []);

  const enterPreviewMode = () => {
    if (isProduction) {
      return {
        success: false,
        error: 'Preview mode er deaktivert i produksjon.'
      };
    }

    setPreviewMode(true);
    setCurrentUser(previewUser);
    setCurrentTenant(defaultTenant);
    setTenants([defaultTenant]);
    setIsAuthenticated(true);
    setPlatformProfiles(previewProfiles);
    setPlatformCustomers(previewCustomers);
    setPlatformAuditLog(previewAuditLog);
    setLoading(false);
    setSuperAdminLoading(false);

    return { success: true };
  };

  const loginDemo = async (email, password) => {
    if (isProduction) {
      return {
        success: false,
        error: 'Demo-login er deaktivert i produksjon.'
      };
    }

    const account = demoAccounts.find(
      item => item.email === email.trim().toLowerCase() && item.password === password
    );

    if (!account) {
      return { success: false, error: 'Demo-e-post eller passord er feil.' };
    }

    const tenant = {
      id: account.id,
      name: account.tenantName,
      logo: account.planId === 'enterprise' ? '🏢' : account.planId === 'pro' ? '🚀' : '⚡',
      plan: account.plan,
      planId: account.planId
    };

    setPreviewMode(false);
    setCurrentTenant(tenant);
    setTenants([tenant]);
    setCurrentUser({
      user_id: account.id,
      email: account.email,
      name: account.name,
      role: 'Customer',
      appMetadataRole: 'Customer',
      isSuperAdmin: false,
      isDemoUser: true,
      demoPlan: account.planId
    });
    setIsAuthenticated(true);
    setLoading(false);

    return {
      success: true,
      session: null,
      isSuperAdmin: false,
      isDemoUser: true,
      plan: account.plan,
      planId: account.planId
    };
  };

  const handleAuthChange = (session) => {
    if (session) {
      const isSuperAdmin = session.user.app_metadata?.role === 'SuperAdmin';
      const appMetadataRole = session.user.app_metadata?.role || '';

      if (!isProduction) {
        console.log('[SUPERADMIN ROLE CHECK]', {
          email: session.user.email,
          appMetadata: session.user.app_metadata,
          userMetadata: session.user.user_metadata,
          role: session.user.app_metadata?.role,
          isSuperAdmin,
          hasUser: Boolean(session.user),
          hasSession: Boolean(session)
        });
      }

      setCurrentUser({
        user_id: session.user.id,
        email: session.user.email,
        name: session.user.user_metadata?.full_name || session.user.email,
        role: session.user.user_metadata?.role || 'Admin',
        appMetadataRole,
        isSuperAdmin
      });
      setCurrentTenant(defaultTenant);
      setTenants([defaultTenant]);
      setIsAuthenticated(true);
      setLoading(false);
      loadAllData();

      if (isSuperAdmin) {
        loadSuperAdminData();
      }
    } else {
      setIsAuthenticated(false);
      setCurrentUser(null);
      setCurrentTenant(defaultTenant);
      setTenants([defaultTenant]);
      setLoading(false);
      setPlatformProfiles([]);
      setPlatformCustomers([]);
      setPlatformAuditLog([]);
      setPlatformEmployeeInvites([]);
    }
  };

  const loadAllData = async () => {
    const activeTenantId = getActiveTenantId();

    if (!activeTenantId) {
      console.error('[CRM DATA LOAD] Missing active tenant. CRM data was not loaded.');
      setCompanies([]);
      setContacts([]);
      setDeals([]);
      setActivities([]);
      setBudgets([]);
      return;
    }

    try {
      const [comp, cont, dl, act, budg] = await Promise.all([
        supabase
          .from('companies_1710925200000')
          .select('*')
          .eq('tenant_id', activeTenantId),
        supabase
          .from('contacts_1710925200000')
          .select('*')
          .eq('tenant_id', activeTenantId),
        supabase
          .from('deals_1710925200000')
          .select('*')
          .eq('tenant_id', activeTenantId),
        supabase
          .from('activities_1710925200000')
          .select('*')
          .eq('tenant_id', activeTenantId),
        supabase
          .from('budgets_1710925200000')
          .select('*')
          .eq('tenant_id', activeTenantId)
      ]);
      setCompanies(Array.isArray(comp?.data) ? comp.data.map(c => ({ ...c, company_id: c.id })) : []);
      setContacts(Array.isArray(cont?.data) ? cont.data.map(c => ({ ...c, contact_id: c.id })) : []);
      setDeals(Array.isArray(dl?.data) ? dl.data.map(d => ({ ...d, deal_id: d.id })) : []);
      setActivities(Array.isArray(act?.data) ? act.data.map(a => ({ ...a, activity_id: a.id })) : []);
      setBudgets(Array.isArray(budg?.data) ? budg.data : []);
    } catch (e) {
      console.error("[CRM AUTH] Data load error (non-blocking):", e.message);
      setCompanies([]);
      setContacts([]);
      setDeals([]);
      setActivities([]);
      setBudgets([]);
    }
  };

  useEffect(() => {
    if (
      !activeTenantId ||
      !isAuthenticated ||
      previewMode ||
      currentUser?.isDemoUser
    ) {
      return;
    }

    loadAllData();
  }, [activeTenantId, isAuthenticated, previewMode, currentUser?.isDemoUser]);

  const addDeal = async (dealData = {}) => {
    try {
      if (!isProduction) {
        console.log('[CRM addDeal] called', dealData);
      }

      const now = new Date().toISOString();
      const inputId = getEntityId(dealData);
      const normalizedStageId =
        dealData.stage_id ||
        dealData.stageId ||
        dealData.pipelineStageId ||
        'stage-1';

      if (!isProduction) {
        console.log('[STAGE FIX addDeal] normalizedStageId', normalizedStageId);
      }

      const activeTenantId = getActiveTenantId();
      const dealRow = {
        ...(isUuid(inputId) ? { id: inputId } : {}),
        title: dealData.title?.trim() || 'Ny Lead',
        value: Number(dealData.value) || 0,
        status: dealData.status || 'Åpen',
        probability: Number(dealData.probability) || 10,
        expected_close_date: dealData.expected_close_date || null,
        company_id: isUuid(dealData.company_id) ? dealData.company_id : null,
        contact_id: isUuid(dealData.contact_id) ? dealData.contact_id : null,
        stage_id: isUuid(normalizedStageId) ? normalizedStageId : null,
        user_id: dealData.user_id || currentUser?.user_id || null,
        tenant_id: activeTenantId
      };

      if (previewMode || currentUser?.isDemoUser) {
        const id = inputId || createId();
        const newDeal = {
          ...dealData,
          ...dealRow,
          id,
          deal_id: id,
          stage_id: normalizedStageId,
          stageId: normalizedStageId,
          createdAt: dealData.createdAt || now,
          created_at: dealData.created_at || now,
          updatedAt: now,
          updated_at: now
        };
        const safeDeals = Array.isArray(deals) ? deals : [];
        const updatedDeals = [...safeDeals, newDeal];

        setDeals(updatedDeals);
        saveToStorage(SALESWORK_DEALS_KEY, updatedDeals);

        if (!isProduction) {
          console.log('[STAGE FIX addDeal] saved demo deal stage_id', newDeal.stage_id);
          console.log('[CRM addDeal] saved', newDeal);
        }
        return { success: true, data: newDeal };
      }

      if (!activeTenantId) {
        return tenantRequiredError();
      }

      const { data, error } = await supabase
        .from('deals_1710925200000')
        .insert(dealRow)
        .select()
        .single();

      if (error) {
        console.error('[CRM addDeal] failed', error);
        return { success: false, error };
      }

      const savedDeal = {
        ...dealData,
        ...data,
        deal_id: data.id,
        updatedAt: data.updated_at || now
      };

      setDeals(current => [...(Array.isArray(current) ? current : []), savedDeal]);
      if (!isProduction) {
        console.log('[CRM addDeal] saved', savedDeal);
      }
      return { success: true, data: savedDeal };
    } catch (error) {
      console.error('[CRM addDeal] failed', error);
      return { success: false, error: { message: error.message || 'Kunne ikke lagre deal/lead.' } };
    }
  };

  const updateDeal = async (dealId, changes = {}) => {
    try {
      const safeDeals = Array.isArray(deals) ? deals : [];
      const existingDeal = safeDeals.find(deal =>
        deal.deal_id === dealId || deal.id === dealId
      );

      if (!existingDeal) {
        return { success: false, error: { message: 'Fant ikke dealen som skulle oppdateres.' } };
      }

      const normalizedStageId =
        changes.stage_id ||
        changes.stageId ||
        changes.pipelineStageId ||
        existingDeal.stage_id ||
        existingDeal.stageId ||
        'stage-1';

      const updatedDeal = {
        ...existingDeal,
        ...changes,
        stage_id: normalizedStageId,
        stageId: normalizedStageId,
        deal_id: dealId,
        updatedAt: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (previewMode || currentUser?.isDemoUser) {
        const updatedDeals = safeDeals.map(deal =>
          deal.deal_id === dealId || deal.id === dealId ? updatedDeal : deal
        );

        setDeals(updatedDeals);
        saveToStorage(SALESWORK_DEALS_KEY, updatedDeals);

        return { success: true, data: updatedDeal };
      }

      const activeTenantId = getActiveTenantId();
      if (!activeTenantId) {
        return tenantRequiredError();
      }

      const updateRow = {
        title: changes.title?.trim() || existingDeal.title,
        value: Number(changes.value ?? existingDeal.value) || 0,
        status: changes.status || existingDeal.status || 'Åpen',
        probability: Number(changes.probability ?? existingDeal.probability) || 10,
        expected_close_date: changes.expected_close_date ?? existingDeal.expected_close_date ?? null,
        company_id: isUuid(changes.company_id ?? existingDeal.company_id)
          ? (changes.company_id ?? existingDeal.company_id)
          : null,
        contact_id: isUuid(changes.contact_id ?? existingDeal.contact_id)
          ? (changes.contact_id ?? existingDeal.contact_id)
          : null,
        stage_id: isUuid(normalizedStageId) ? normalizedStageId : null
      };

      const { data, error } = await supabase
        .from('deals_1710925200000')
        .update(updateRow)
        .eq('id', dealId)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (error) {
        console.error('[CRM updateDeal] failed', error);
        return { success: false, error };
      }

      const savedDeal = {
        ...updatedDeal,
        ...data,
        deal_id: data.id,
        stageId: data.stage_id
      };

      setDeals(current =>
        (Array.isArray(current) ? current : []).map(deal =>
          deal.deal_id === dealId || deal.id === dealId ? savedDeal : deal
        )
      );

      return { success: true, data: savedDeal };
    } catch (error) {
      console.error('[CRM updateDeal] failed', error);
      return { success: false, error: { message: error.message || 'Kunne ikke oppdatere dealen.' } };
    }
  };

  const deleteDeal = async dealId => {
    try {
      if (previewMode || currentUser?.isDemoUser) {
        const safeDeals = Array.isArray(deals) ? deals : [];
        const updatedDeals = safeDeals.filter(deal =>
          deal.deal_id !== dealId && deal.id !== dealId
        );

        setDeals(updatedDeals);
        saveToStorage(SALESWORK_DEALS_KEY, updatedDeals);

        return { success: true };
      }

      const activeTenantId = getActiveTenantId();
      if (!activeTenantId) {
        return tenantRequiredError();
      }

      const { error } = await supabase
        .from('deals_1710925200000')
        .delete()
        .eq('id', dealId)
        .eq('tenant_id', activeTenantId);

      if (error) {
        console.error('[CRM deleteDeal] failed', error);
        return { success: false, error };
      }

      setDeals(current =>
        (Array.isArray(current) ? current : []).filter(deal =>
          deal.deal_id !== dealId && deal.id !== dealId
        )
      );

      return { success: true };
    } catch (error) {
      console.error('[CRM deleteDeal] failed', error);
      return { success: false, error: { message: error.message || 'Kunne ikke slette dealen.' } };
    }
  };

  const addCompany = async (companyData = {}) => {
    try {
      if (!isProduction) {
        console.log('[COMPANY SAVE] mode', previewMode || currentUser?.isDemoUser ? 'preview/localStorage' : 'supabase');
      }

      const now = new Date().toISOString();
      const inputId = getEntityId(companyData);
      const activeTenantId = getActiveTenantId();
      const companyRow = {
        ...(isUuid(inputId) ? { id: inputId } : {}),
        name: companyData.name?.trim() || 'Ukjent selskap',
        industry: companyData.industry || '',
        website: companyData.website || '',
        city: companyData.city || '',
        status: companyData.status || 'Lead',
        user_id: currentUser?.user_id || null,
        tenant_id: activeTenantId
      };

      if (previewMode || currentUser?.isDemoUser) {
        const id = inputId || createId();
        const newCompany = {
          ...companyData,
          ...companyRow,
          id,
          company_id: id,
          createdAt: companyData.createdAt || now,
          created_at: companyData.created_at || now,
          updatedAt: now,
          updated_at: now
        };
        const safeCompanies = Array.isArray(companies) ? companies : [];
        const updatedCompanies = [...safeCompanies, newCompany];

        setCompanies(updatedCompanies);
        saveToStorage(SALESWORK_COMPANIES_KEY, updatedCompanies);

        if (!isProduction) {
          console.log('[COMPANY SAVE] saved company', newCompany);
          console.log('[COMPANY SAVE] localStorage companies', localStorage.getItem(SALESWORK_COMPANIES_KEY));
        }

        return { success: true, data: newCompany };
      }

      if (!activeTenantId) {
        return tenantRequiredError();
      }

      const { data, error } = await supabase
        .from('companies_1710925200000')
        .insert(companyRow)
        .select()
        .single();

      if (error) {
        console.error('[CRM addCompany] failed', error);
        return { success: false, error };
      }

      const savedCompany = { ...companyData, ...data, company_id: data.id };
      setCompanies(current => [...(Array.isArray(current) ? current : []), savedCompany]);
      return { success: true, data: savedCompany };
    } catch (error) {
      console.error('[CRM addCompany] failed', error);
      return { success: false, error: { message: error.message || 'Kunne ikke lagre selskapet.' } };
    }
  };

  const updateCompany = async (companyId, changes = {}) => {
    try {
      const safeCompanies = Array.isArray(companies) ? companies : [];
      const existingCompany = safeCompanies.find(company =>
        company.company_id === companyId || company.id === companyId
      );

      if (!existingCompany) {
        return { success: false, error: { message: 'Fant ikke selskapet som skulle oppdateres.' } };
      }

      const updatedCompany = {
        ...existingCompany,
        ...changes,
        company_id: companyId,
        updatedAt: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (previewMode || currentUser?.isDemoUser) {
        const updatedCompanies = safeCompanies.map(company =>
          company.company_id === companyId || company.id === companyId
            ? updatedCompany
            : company
        );

        setCompanies(updatedCompanies);
        saveToStorage(SALESWORK_COMPANIES_KEY, updatedCompanies);

        return { success: true, data: updatedCompany };
      }

      const activeTenantId = getActiveTenantId();
      if (!activeTenantId) {
        return tenantRequiredError();
      }

      const updateRow = {
        name: changes.name?.trim() || existingCompany.name,
        industry: changes.industry ?? existingCompany.industry ?? '',
        website: changes.website ?? existingCompany.website ?? '',
        city: changes.city ?? existingCompany.city ?? '',
        status: changes.status || existingCompany.status || 'Lead'
      };

      const { data, error } = await supabase
        .from('companies_1710925200000')
        .update(updateRow)
        .eq('id', companyId)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (error) {
        console.error('[CRM updateCompany] failed', error);
        return { success: false, error };
      }

      const savedCompany = { ...updatedCompany, ...data, company_id: data.id };
      setCompanies(current =>
        (Array.isArray(current) ? current : []).map(company =>
          company.company_id === companyId || company.id === companyId
            ? savedCompany
            : company
        )
      );

      return { success: true, data: savedCompany };
    } catch (error) {
      console.error('[CRM updateCompany] failed', error);
      return { success: false, error: { message: error.message || 'Kunne ikke oppdatere selskapet.' } };
    }
  };

  const addContact = async (contactData = {}) => {
    try {
      const now = new Date().toISOString();
      const inputId = getEntityId(contactData);
      const activeTenantId = getActiveTenantId();
      const contactRow = {
        ...(isUuid(inputId) ? { id: inputId } : {}),
        company_id: isUuid(contactData.company_id) ? contactData.company_id : null,
        name: contactData.name?.trim() || 'Ukjent kontakt',
        email: contactData.email?.trim() || '',
        phone: contactData.phone || '',
        role: contactData.role || contactData.title || '',
        role_in_buying: contactData.role_in_buying || 'User',
        linkedin_url: contactData.linkedin_url || '',
        score: Number(contactData.score) || 0,
        user_id: currentUser?.user_id || null,
        tenant_id: activeTenantId
      };

      if (previewMode || currentUser?.isDemoUser) {
        const id = inputId || createId();
        const newContact = {
          ...contactData,
          ...contactRow,
          id,
          contact_id: id,
          createdAt: contactData.createdAt || now,
          created_at: contactData.created_at || now,
          updatedAt: now,
          updated_at: now
        };
        const safeContacts = Array.isArray(contacts) ? contacts : [];
        const updatedContacts = [...safeContacts, newContact];

        setContacts(updatedContacts);
        saveToStorage(SALESWORK_CONTACTS_KEY, updatedContacts);

        return { success: true, data: newContact };
      }

      if (!activeTenantId) {
        return tenantRequiredError();
      }

      const { data, error } = await supabase
        .from('contacts_1710925200000')
        .insert(contactRow)
        .select()
        .single();

      if (error) {
        console.error('[CRM addContact] failed', error);
        return { success: false, error };
      }

      const savedContact = { ...contactData, ...data, contact_id: data.id };
      setContacts(current => [...(Array.isArray(current) ? current : []), savedContact]);
      return { success: true, data: savedContact };
    } catch (error) {
      console.error('[CRM addContact] failed', error);
      return { success: false, error: { message: error.message || 'Kunne ikke lagre kontakten.' } };
    }
  };

  const updateContact = async (contactId, changes = {}) => {
    try {
      const safeContacts = Array.isArray(contacts) ? contacts : [];
      const existingContact = safeContacts.find(contact =>
        contact.contact_id === contactId || contact.id === contactId
      );

      if (!existingContact) {
        return { success: false, error: { message: 'Fant ikke kontakten som skulle oppdateres.' } };
      }

      const updatedContact = {
        ...existingContact,
        ...changes,
        contact_id: contactId,
        updatedAt: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (previewMode || currentUser?.isDemoUser) {
        const updatedContacts = safeContacts.map(contact =>
          contact.contact_id === contactId || contact.id === contactId
            ? updatedContact
            : contact
        );

        setContacts(updatedContacts);
        saveToStorage(SALESWORK_CONTACTS_KEY, updatedContacts);

        return { success: true, data: updatedContact };
      }

      const activeTenantId = getActiveTenantId();
      if (!activeTenantId) {
        return tenantRequiredError();
      }

      const updateRow = {
        company_id: isUuid(changes.company_id ?? existingContact.company_id)
          ? (changes.company_id ?? existingContact.company_id)
          : null,
        name: changes.name?.trim() || existingContact.name,
        email: changes.email?.trim() || existingContact.email || '',
        phone: changes.phone ?? existingContact.phone ?? '',
        role: changes.role ?? changes.title ?? existingContact.role ?? '',
        role_in_buying: changes.role_in_buying || existingContact.role_in_buying || 'User',
        linkedin_url: changes.linkedin_url ?? existingContact.linkedin_url ?? '',
        score: Number(changes.score ?? existingContact.score) || 0
      };

      const { data, error } = await supabase
        .from('contacts_1710925200000')
        .update(updateRow)
        .eq('id', contactId)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (error) {
        console.error('[CRM updateContact] failed', error);
        return { success: false, error };
      }

      const savedContact = { ...updatedContact, ...data, contact_id: data.id };
      setContacts(current =>
        (Array.isArray(current) ? current : []).map(contact =>
          contact.contact_id === contactId || contact.id === contactId
            ? savedContact
            : contact
        )
      );

      return { success: true, data: savedContact };
    } catch (error) {
      console.error('[CRM updateContact] failed', error);
      return { success: false, error: { message: error.message || 'Kunne ikke oppdatere kontakten.' } };
    }
  };

  const addActivity = async (activity) => {
    const activeTenantId = getActiveTenantId();
    const activityRow = getActivityRow(
      activity,
      currentUser?.user_id,
      activeTenantId
    );

    if (!activityRow.title) {
      return { success: false, error: { message: 'Aktiviteten må ha en tittel.' } };
    }

    if (previewMode || currentUser?.isDemoUser) {
      const id = `preview-activity-${Date.now()}`;
      const previewActivity = {
        ...activity,
        ...activityRow,
        id,
        activity_id: id,
        created_at: new Date().toISOString()
      };

      setActivities(current => [...(current || []), previewActivity]);
      return { success: true, data: previewActivity };
    }

    if (!activeTenantId) {
      return tenantRequiredError();
    }

    const { data, error } = await supabase
      .from('activities_1710925200000')
      .insert(activityRow)
      .select()
      .single();

    if (error) {
      console.error('[CRM ACTIVITY CREATE]', error);
      return { success: false, error };
    }

    const savedActivity = { ...data, activity_id: data.id };
    setActivities(current => [...(current || []), savedActivity]);

    return { success: true, data: savedActivity };
  };

  const updateActivity = async (activityId, changes) => {
    if (!activityId) {
      return { success: false, error: { message: 'Mangler aktivitets-ID.' } };
    }

    const activeTenantId = getActiveTenantId();
    const activityRow = getActivityRow(
      changes,
      currentUser?.user_id,
      activeTenantId
    );
    delete activityRow.user_id;

    if (!activityRow.title) {
      return { success: false, error: { message: 'Aktiviteten må ha en tittel.' } };
    }

    if (previewMode || currentUser?.isDemoUser) {
      const updatedActivity = {
        ...changes,
        ...activityRow,
        id: activityId,
        activity_id: activityId
      };

      setActivities(current =>
        (current || []).map(activity =>
          activity.activity_id === activityId || activity.id === activityId
            ? { ...activity, ...updatedActivity }
            : activity
        )
      );

      return { success: true, data: updatedActivity };
    }

    if (!activeTenantId) {
      return tenantRequiredError();
    }

    const { data, error } = await supabase
      .from('activities_1710925200000')
      .update(activityRow)
      .eq('id', activityId)
      .eq('tenant_id', activeTenantId)
      .select()
      .single();

    if (error) {
      console.error('[CRM ACTIVITY UPDATE]', error);
      return { success: false, error };
    }

    const updatedActivity = { ...data, activity_id: data.id };
    setActivities(current =>
      (current || []).map(activity =>
        activity.activity_id === activityId || activity.id === activityId
          ? updatedActivity
          : activity
      )
    );

    return { success: true, data: updatedActivity };
  };

  const loadSuperAdminData = async () => {
    if (!isProduction && previewMode) {
      setPlatformProfiles(previewProfiles);
      setPlatformCustomers(previewCustomers);
      setPlatformAuditLog(previewAuditLog);
      return;
    }

    setSuperAdminLoading(true);

    try {
      const [profiles, customers, audit, invites] = await Promise.all([
        supabase
          .from('platform_profiles_1788695942781')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('platform_customers_1788695942781')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('platform_audit_log_1788695942781')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('platform_employee_invites_1788697000000')
          .select('*')
          .order('created_at', { ascending: false })
      ]);

      if (profiles.error) console.error('[SUPERADMIN PROFILES]', profiles.error);
      if (customers.error) console.error('[SUPERADMIN CUSTOMERS]', customers.error);
      if (audit.error) console.error('[SUPERADMIN AUDIT]', audit.error);
      if (invites.error) console.error('[SUPERADMIN EMPLOYEE INVITES]', invites.error);

      setPlatformProfiles(Array.isArray(profiles?.data) ? profiles.data : []);
      setPlatformCustomers(Array.isArray(customers?.data) ? customers.data : []);
      setPlatformAuditLog(Array.isArray(audit?.data) ? audit.data : []);
      setPlatformEmployeeInvites(Array.isArray(invites?.data) ? invites.data : []);
    } catch (error) {
      console.error('[SUPERADMIN DATA LOAD]', error);
      setPlatformProfiles([]);
      setPlatformCustomers([]);
      setPlatformAuditLog([]);
      setPlatformEmployeeInvites([]);
    } finally {
      setSuperAdminLoading(false);
    }
  };

  const createEmployeeInvite = async ({ name, email, role }) => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!name.trim() || !normalizedEmail || !['SuperAdmin', 'PlatformAdmin', 'Support', 'Sales', 'Finance'].includes(role)) {
      return { success: false, error: { message: 'Navn, e-post og gyldig rolle må fylles ut.' } };
    }

    if (!isProduction && previewMode) {
      const invite = {
        id: `preview-invite-${Date.now()}`,
        key: `preview-invite-${Date.now()}`,
        name: name.trim(),
        email: normalizedEmail,
        role,
        status: 'Pending',
        created_at: new Date().toISOString(),
        isInvite: true
      };

      setPlatformProfiles(current => [...(current || []), invite]);
      setPlatformEmployeeInvites(current => [...(current || []), invite]);
      return { success: true, data: invite };
    }

    const { data, error } = await supabase
      .from('platform_employee_invites_1788697000000')
      .insert({
        name: name.trim(),
        email: normalizedEmail,
        role,
        status: 'Pending',
        created_by: currentUser?.user_id
      })
      .select()
      .single();

    if (error) {
      console.error('[SUPERADMIN EMPLOYEE CREATE]', error);
      return { success: false, error };
    }

    setPlatformEmployeeInvites(current => [data, ...(current || [])]);

    await supabase.from('platform_audit_log_1788695942781').insert({
      actor_id: currentUser?.user_id,
      action: 'employee.invited',
      entity_type: 'platform_employee_invite',
      entity_id: data.id,
      details: { email: normalizedEmail, role }
    });

    return { success: true, data };
  };

  const updateEmployee = async (employee, changes) => {
    if (!isProduction && previewMode) {
      setPlatformProfiles(current =>
        (current || []).map(item =>
          item.key === employee.key || item.id === employee.id
            ? { ...item, ...changes }
            : item
        )
      );
      return { success: true, data: { ...employee, ...changes } };
    }

    if (employee.isInvite) {
      const { data, error } = await supabase
        .from('platform_employee_invites_1788697000000')
        .update(changes)
        .eq('id', employee.id)
        .select()
        .single();

      if (error) {
        console.error('[SUPERADMIN INVITE UPDATE]', error);
        return { success: false, error };
      }

      setPlatformEmployeeInvites(current =>
        (current || []).map(item => item.id === employee.id ? data : item)
      );
      return { success: true, data };
    }

    const { data, error } = await supabase.rpc('update_platform_employee_1788697000000', {
      target_profile_id: employee.id,
      new_role: changes.role || employee.role,
      new_status: changes.status || employee.status
    });

    if (error) {
      console.error('[SUPERADMIN EMPLOYEE UPDATE]', error);
      return { success: false, error };
    }

    setPlatformProfiles(current =>
      (current || []).map(item => item.id === employee.id ? data : item)
    );

    return { success: true, data };
  };

  const mergeCompanies = async (sourceCompanyId, targetCompanyId) => {
    if (!sourceCompanyId || !targetCompanyId || sourceCompanyId === targetCompanyId) {
      return { success: false, error: { message: 'Velg to forskjellige selskaper.' } };
    }

    if (!isProduction && previewMode) {
      const safeCompanies = Array.isArray(companies) ? companies : [];
      const updatedCompanies = safeCompanies.filter(company =>
        company.company_id !== sourceCompanyId
      );

      setCompanies(updatedCompanies);
      saveToStorage(SALESWORK_COMPANIES_KEY, updatedCompanies);

      return {
        success: true,
        data: {
          source_company_id: sourceCompanyId,
          target_company_id: targetCompanyId,
          moved_contacts: 0,
          moved_deals: 0
        }
      };
    }

    const { data, error } = await supabase.rpc('merge_companies_1788700000000', {
      source_company_id: sourceCompanyId,
      target_company_id: targetCompanyId
    });

    if (error) {
      console.error('[CRM COMPANY MERGE]', error);
      return { success: false, error };
    }

    await loadAllData();
    return { success: true, data };
  };

  const createPlatformCustomer = async (customer) => {
    const companyName = customer.company_name?.trim() || '';
    const contactEmail = customer.contact_email?.trim().toLowerCase() || '';
    const plan = customer.plan || 'Trial';
    const status = customer.status || 'Active';
    const seats = Number(customer.seats);
    const monthlyValue = Number(customer.monthly_value);

    if (!companyName || !contactEmail) {
      return {
        success: false,
        error: { message: 'Kundenavn og kontakt-e-post må fylles ut.' }
      };
    }

    if (!Number.isFinite(seats) || seats < 1) {
      return {
        success: false,
        error: { message: 'Antall seter må være minst 1.' }
      };
    }

    if (!Number.isFinite(monthlyValue) || monthlyValue < 0) {
      return {
        success: false,
        error: { message: 'Månedlig verdi kan ikke være negativ.' }
      };
    }

    const customerRow = {
      company_name: companyName,
      contact_email: contactEmail,
      plan,
      status,
      seats,
      monthly_value: monthlyValue,
      started_at: customer.started_at || new Date().toISOString().slice(0, 10),
      notes: customer.notes?.trim() || ''
    };

    if (!isProduction && previewMode) {
      const previewCustomer = {
        id: `preview-customer-${Date.now()}`,
        ...customerRow,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      setPlatformCustomers(current => [previewCustomer, ...(current || [])]);
      return { success: true, data: previewCustomer };
    }

    const { data, error } = await supabase
      .from('platform_customers_1788695942781')
      .insert(customerRow)
      .select()
      .single();

    if (error) {
      console.error('[SUPERADMIN CUSTOMER CREATE]', error);
      return { success: false, error };
    }

    setPlatformCustomers(current => [data, ...(current || [])]);

    await supabase.from('platform_audit_log_1788695942781').insert({
      actor_id: currentUser?.user_id,
      action: 'customer.created',
      entity_type: 'platform_customer',
      entity_id: data.id,
      details: {
        company_name: companyName,
        contact_email: contactEmail,
        plan,
        status
      }
    });

    return { success: true, data };
  };

  const updatePlatformCustomer = async (customerId, changes) => {
    if (!isProduction && previewMode) {
      setPlatformCustomers(current =>
        (current || []).map(customer =>
          customer.id === customerId ? { ...customer, ...changes } : customer
        )
      );
      return { success: true, data: { id: customerId, ...changes } };
    }

    const { data, error } = await supabase
      .from('platform_customers_1788695942781')
      .update(changes)
      .eq('id', customerId)
      .select()
      .single();

    if (error) {
      console.error('[SUPERADMIN CUSTOMER UPDATE]', error);
      return { success: false, error };
    }

    setPlatformCustomers(current =>
      (current || []).map(customer =>
        customer.id === customerId ? data : customer
      )
    );

    await supabase.from('platform_audit_log_1788695942781').insert({
      actor_id: currentUser?.user_id,
      action: 'customer.updated',
      entity_type: 'platform_customer',
      entity_id: customerId,
      details: changes
    });

    return { success: true, data };
  };

  const login = async (email, password) => {
    if (!isProduction && previewMode) {
      return {
        success: true,
        session: null,
        appMetadataRole: 'SuperAdmin',
        isSuperAdmin: true
      };
    }

    if (!isProduction) {
      console.log('[AUTH] signInWithPassword calling for:', email);
    }
    try {
      const result = await supabase.auth.signInWithPassword({ email, password });
      
      if (!isProduction) {
        console.log('[AUTH ERROR RAW]', result.error);
      }
      if (result.error) {
        if (!isProduction) {
          console.log('[AUTH ERROR MESSAGE]', result.error.message);
          console.log('[AUTH ERROR STATUS]', result.error.status);
        }
        return { success: false, error: result.error.message, status: result.error.status };
      }

      if (!isProduction) {
        console.log('[AUTH SUCCESS] User:', result.data.user?.id);
        console.log('[AUTH SUCCESS DATA]', {
          hasUser: Boolean(result.data.user),
          hasSession: Boolean(result.data.session),
          appMetadata: result.data.user?.app_metadata,
          userMetadata: result.data.user?.user_metadata
        });
      }

      const appMetadataRole = result.data.user?.app_metadata?.role || '';
      const isSuperAdmin = appMetadataRole === 'SuperAdmin';

      return {
        success: true,
        session: result.data.session,
        appMetadataRole,
        isSuperAdmin
      };
    } catch (e) {
      console.error('[AUTH CRASH]', e);
      return { success: false, error: e.message };
    }
  };

  const logout = async () => {
    if (!isProduction) {
      console.log('[LOGOUT] preserving demo CRM localStorage data');
    }

    if (currentUser?.isDemoUser) {
      setIsAuthenticated(false);
      setCurrentUser(null);
      setCurrentTenant(defaultTenant);
      setTenants([defaultTenant]);
      return;
    }

    if (!isProduction && previewMode) return;
    await supabase.auth.signOut();
  };

  const switchTenant = tenantId => {
    const tenant = tenants.find(item => item.id === tenantId);
    if (tenant) setCurrentTenant(tenant);
  };

  const value = {
    isAuthenticated, loading, currentUser, companies, contacts, deals, activities, budgets, login, loginDemo, demoAccounts, logout,
    previewMode, enterPreviewMode,
    addActivity, updateActivity,
    addDeal, updateDeal, deleteDeal,
    addCompany, updateCompany,
    addContact, updateContact,
    mergeCompanies,
    platformProfiles, platformCustomers, platformAuditLog, platformEmployeeInvites,
    superAdminLoading, loadSuperAdminData, createEmployeeInvite, updateEmployee,
    createPlatformCustomer, updatePlatformCustomer,
    notifications: [], users: [], projects: [], departments: [], sentContracts: [], massEmailCampaigns: [],
    customerRooms: [], contractTemplates: [], emailTemplates: [], products: [], systemEmailSettings: [],
    knowledgeArticles: [], integrations: [], customFieldDefinitions: [], tenants, currentTenant, switchTenant, roles: {},
    pipelineStages: [{ stage_id: 'stage-1', name: 'Lead', color: 'bg-blue-100' }],
    permissions: { canViewAll: true, canImportBrreg: true, canSettings: true, canViewCRM: true, canViewReports: true, canOutreach: true },
    themeSettings: { primaryColor: '#2563eb', sidebarColor: '#172554' },
    toggleHelp: () => {}, updateCookieConsent: () => {}, toggleDarkMode: () => {}, updateTheme: () => {},
    toggleIntegration: () => {}, getAccessibleUsers: () => []
  };

  return <CRMContext.Provider value={value}>{children}</CRMContext.Provider>;
};